import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'helpers/proptypes'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Form, Menu, Segment, Icon, Select, Button } from 'semantic-ui-react'
import { getSortedLanguages } from 'redux/entities/selectors'
import { buildSelectOptions } from 'helpers/forms'

export function TabsDescriptionsByLang({
  describes,
  children,
  field,
  usedLanguages,
  addDescriptionsForLanguage,
  removeDescriptionsForLanguage,
}) {
  const { t } = useTranslation()
  const languages = useSelector(getSortedLanguages).filter(({ translated }) => translated)

  const options = useMemo(() => {
    // for the partners dashboard, only english descriptions are taken into account *shrug*
    const optionsLanguages =
      describes === 'partner_event' ? languages.filter((language) => language.locale === 'en') : languages
    return buildSelectOptions(optionsLanguages.filter((language) => !usedLanguages.includes(language)))
  }, [describes, languages, usedLanguages])

  const [activeLanguageId, setActiveLanguageId] = useState(undefined)
  const [selectedLanguageId, setSelectedLanguageId] = useState(undefined)

  useEffect(() => {
    if (!activeLanguageId && usedLanguages.size > 0) setActiveLanguageId(parseInt(usedLanguages.first().id, 10))
  }, [activeLanguageId, usedLanguages])

  function handleDescriptionsAdd(e) {
    e?.preventDefault()

    if (!selectedLanguageId) return
    setActiveLanguageId(selectedLanguageId)
    addDescriptionsForLanguage(selectedLanguageId)
  }

  function handleDescriptionsRemove(language) {
    if (activeLanguageId === language.id) {
      const indexOfRemovedLanguage = usedLanguages.indexOf(language)
      const nextActiveIndex = indexOfRemovedLanguage === 0 ? 1 : indexOfRemovedLanguage - 1
      setActiveLanguageId(usedLanguages.get(nextActiveIndex).id)
    }
    removeDescriptionsForLanguage(language.id)
  }

  return (
    <div style={{ marginBottom: '16px' }}>
      <Form.Field inline>
        <label>
          {usedLanguages.size > 0 &&
            t('Experiences::Summary::Add {{describes}} {{field}} in', { describes: describes, field: field })}
          {usedLanguages.size === 0 &&
            t('Experiences::Summary::Create {{describes}} {{field}} in', { describes: describes, field: field })}
        </label>
        <Select
          search
          value={selectedLanguageId}
          onChange={(_, { value }) => setSelectedLanguageId(value)}
          options={options}
        />
        <Button onClick={handleDescriptionsAdd}>{t('Experiences::Summary::OK')}</Button>
      </Form.Field>

      <Menu attached='top' tabular>
        {usedLanguages.map((language) => (
          <Menu.Item key={language.id} name={language.title} active={language.id === activeLanguageId}>
            <div
              style={{
                display: 'inline-block',
                marginRight: '8px',
                cursor: 'pointer',
              }}
              onClick={() => setActiveLanguageId(language.id)}
            >
              {language.title}
            </div>
            <div style={{ cursor: 'pointer' }}>
              <Icon name='close' circular bordered onClick={() => handleDescriptionsRemove(language)} />
            </div>
          </Menu.Item>
        ))}
      </Menu>
      {usedLanguages.size > 0 && <Segment attached='bottom'>{children({ languageId: activeLanguageId })}</Segment>}
    </div>
  )
}

TabsDescriptionsByLang.propTypes = {
  describes: PropTypes.string.isRequired,
  children: PropTypes.func.isRequired,
  field: PropTypes.string,
  usedLanguages: PropTypes.immutable.list.isRequired,
  addDescriptionsForLanguage: PropTypes.func.isRequired,
  removeDescriptionsForLanguage: PropTypes.func.isRequired,
}

TabsDescriptionsByLang.defaultProps = {
  field: 'description',
}
